import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Logo } from '../components/Logo';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Check, CreditCard, Building2, FileText, Bot, Sparkles } from 'lucide-react';

const PLANS = {
    artigiano: { name: 'ARTIGIANO', price_m: 99, price_y: 1069, kind: 'base' },
    pmi: { name: 'PMI', price_m: 149, price_y: 1609, kind: 'base' },
    impresa: { name: 'IMPRESA AI', price_m: 299, price_y: 3229, kind: 'base' },
};

const BASE_PLAN_IDS = ['artigiano', 'pmi', 'impresa'];
const PLUS_PLAN_IDS = [];

export default function Checkout() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [yearly, setYearly] = useState(searchParams.get('period') === 'yearly');
    // Backward-compat: redirect deprecated plan ids to IMPRESA AI (since all AI agents are now bundled in it).
    const _rawPlanId = searchParams.get('plan') || 'pmi';
    const _planAlias = (id) => (['forza_vendita_pro', 'forza_vendita_elite', 'agenti_ai'].includes(id) ? 'impresa' : id);
    const [planId, setPlanId] = useState(_planAlias(_rawPlanId));
    const [company, setCompany] = useState({
        company_name: '', piva: '', sdi: '0000000', pec: '', email: '', full_name: '', indirizzo: '', cap: '', comune: '', provincia: '',
    });
    const [terms, setTerms] = useState(false);
    const [loading, setLoading] = useState(false);

    const plan = PLANS[planId];
    const period = yearly ? 'yearly' : 'monthly';
    const packageId = `${planId}_${period}`;
    const amount = yearly ? plan.price_y : plan.price_m;

    const handleStripeCheckout = async () => {
        if (!terms) {
            toast.error('Accetta i termini per procedere');
            return;
        }
        if (!company.company_name || !company.piva || !company.email) {
            toast.error('Compila i campi obbligatori');
            setStep(2);
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/payments/checkout/session', {
                package_id: packageId,
                origin_url: window.location.origin,
                email: company.email,
                company_name: company.company_name,
            });
            // Store company data for after success
            localStorage.setItem('cantiere_pending_company', JSON.stringify(company));
            window.location.href = res.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Errore Stripe');
            setLoading(false);
        }
    };

    const steps = ['Piano', 'Dati azienda', 'Pagamento', 'Conferma'];

    return (
        <div className="min-h-screen bg-[hsl(0_0%_98%)] flex flex-col">
            <div className="p-6 border-b border-[hsl(var(--border))] bg-white">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Logo />
                    <Link to="/" className="text-sm text-[hsl(var(--muted-foreground))] hover:underline">← Torna al sito</Link>
                </div>
            </div>

            <div className="max-w-6xl mx-auto w-full px-4 py-8 grid lg:grid-cols-[1fr_400px] gap-8">
                <div>
                    {/* Stepper */}
                    <div className="flex items-center gap-2 mb-8">
                        {steps.map((label, i) => {
                            const n = i + 1;
                            const active = step === n;
                            const done = step > n;
                            return (
                                <React.Fragment key={i}>
                                    <div className={`flex items-center gap-2 ${done ? 'text-[hsl(var(--accent))]' : active ? 'text-[hsl(220_13%_10%)]' : 'text-[hsl(var(--muted-foreground))]'}`} data-testid={`checkout-stepper-step-${n}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 ${done ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-[hsl(220_13%_10%)]' : active ? 'border-[hsl(var(--accent))] bg-white' : 'border-[hsl(var(--border))]'}`}>
                                            {done ? <Check className="w-4 h-4" /> : n}
                                        </div>
                                        <span className="hidden md:inline text-sm font-medium">{label}</span>
                                    </div>
                                    {n < steps.length && <div className="flex-1 h-px bg-[hsl(var(--border))]" />}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <Card className="p-6">
                        {step === 1 && (
                            <div>
                                <h2 className="font-display text-3xl mb-2">Scegli il tuo piano</h2>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">Puoi cambiare in qualsiasi momento.</p>
                                <div className="inline-flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-1 mb-5">
                                    <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${!yearly ? 'bg-white shadow' : 'text-[hsl(var(--muted-foreground))]'}`}>Mensile</button>
                                    <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${yearly ? 'bg-white shadow' : 'text-[hsl(var(--muted-foreground))]'}`}>Annuale {yearly && <span className="text-[hsl(var(--accent))] ml-1">-10%</span>}</button>
                                </div>

                                <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-2">PIANI BASE</div>
                                <div className="space-y-3 mb-5">
                                    {BASE_PLAN_IDS.map((id) => {
                                        const p = PLANS[id];
                                        return (
                                            <button key={id} onClick={() => setPlanId(id)} className={`w-full flex items-center justify-between p-4 rounded-lg border-2 text-left transition-colors ${planId === id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))]'}`} data-testid={`checkout-plan-${id}`}>
                                                <div>
                                                    <div className="font-display text-xl">{p.name}</div>
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{yearly ? `€${p.price_y}/anno` : `€${p.price_m}/mese`}</div>
                                                </div>
                                                <div className="font-display text-3xl">€{yearly ? Math.round(p.price_y/12) : p.price_m}<span className="text-sm text-[hsl(var(--muted-foreground))]">/mese</span></div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))]">SERVIZI INCLUSI</div>
                                    <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">IMPRESA AI</span>
                                </div>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                                    Tutti i 7 agenti AI (Lead Finder, Risponditore Mail, Preventivista, Recupero Crediti, Follow-up, CRM, Fatturatore SAL) sono inclusi nel piano <strong>IMPRESA AI</strong>. Nessun costo aggiuntivo.
                                </p>

                                <Button onClick={() => setStep(2)} className="w-full mt-6 bg-[hsl(220_13%_10%)] text-white h-11" data-testid="checkout-step1-next">Continua →</Button>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <h2 className="font-display text-3xl mb-2">Dati azienda</h2>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">Per fatturazione elettronica.</p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="sm:col-span-2"><Label>Ragione sociale*</Label><Input required value={company.company_name} onChange={(e) => setCompany({...company, company_name: e.target.value})} className="mt-1" data-testid="checkout-company-name" /></div>
                                    <div><Label>P.IVA*</Label><Input required value={company.piva} onChange={(e) => setCompany({...company, piva: e.target.value})} className="mt-1" data-testid="checkout-piva" /></div>
                                    <div><Label>Codice SDI</Label><Input maxLength={7} value={company.sdi} onChange={(e) => setCompany({...company, sdi: e.target.value})} className="mt-1" /></div>
                                    <div className="sm:col-span-2"><Label>PEC (opzionale)</Label><Input type="email" value={company.pec} onChange={(e) => setCompany({...company, pec: e.target.value})} className="mt-1" /></div>
                                    <div><Label>Email*</Label><Input type="email" required value={company.email} onChange={(e) => setCompany({...company, email: e.target.value})} className="mt-1" data-testid="checkout-email" /></div>
                                    <div><Label>Referente*</Label><Input required value={company.full_name} onChange={(e) => setCompany({...company, full_name: e.target.value})} className="mt-1" /></div>
                                    <div className="sm:col-span-2"><Label>Indirizzo</Label><Input value={company.indirizzo} onChange={(e) => setCompany({...company, indirizzo: e.target.value})} className="mt-1" /></div>
                                    <div><Label>CAP</Label><Input value={company.cap} onChange={(e) => setCompany({...company, cap: e.target.value})} className="mt-1" /></div>
                                    <div><Label>Comune</Label><Input value={company.comune} onChange={(e) => setCompany({...company, comune: e.target.value})} className="mt-1" /></div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <Button variant="outline" onClick={() => setStep(1)}>Indietro</Button>
                                    <Button onClick={() => setStep(3)} disabled={!company.company_name || !company.piva || !company.email} className="flex-1 bg-[hsl(220_13%_10%)] text-white h-11" data-testid="checkout-step2-next">Continua →</Button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div>
                                <h2 className="font-display text-3xl mb-2">Pagamento</h2>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">Verrai reindirizzato a Stripe (sicuro).</p>
                                <div className="p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 mb-5">
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="w-6 h-6 text-[hsl(var(--accent))]" />
                                        <div>
                                            <div className="font-semibold">Carta di credito / debito</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">Pagamento gestito da Stripe Inc.</div>
                                        </div>
                                    </div>
                                </div>
                                <label className="flex items-start gap-2 text-sm mb-5 cursor-pointer">
                                    <Checkbox checked={terms} onCheckedChange={setTerms} className="mt-0.5" data-testid="checkout-terms" />
                                    <span>Accetto i <a className="underline">Termini di servizio</a> e l’<a className="underline">Informativa privacy</a></span>
                                </label>
                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={() => setStep(2)}>Indietro</Button>
                                    <Button onClick={handleStripeCheckout} disabled={loading || !terms} className="flex-1 bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11" data-testid="checkout-pay-button">
                                        {loading ? 'Reindirizzamento…' : `Paga €${amount}`}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Summary sidebar */}
                <div>
                    <Card className="p-6 sticky top-6">
                        <h3 className="font-display text-xl mb-4">RIEPILOGO</h3>
                        <div className="space-y-3 mb-4">
                            <div className="flex justify-between text-sm">
                                <span className="text-[hsl(var(--muted-foreground))]">Piano</span>
                                <span className="font-semibold">{plan.name}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-[hsl(var(--muted-foreground))]">Periodo</span>
                                <span className="font-semibold">{yearly ? 'Annuale' : 'Mensile'}</span>
                            </div>
                            {yearly && (
                                <div className="flex justify-between text-sm text-[hsl(145_55%_34%)]">
                                    <span>Risparmio</span>
                                    <span className="font-semibold">-€{(plan.price_m * 12) - plan.price_y}</span>
                                </div>
                            )}
                        </div>
                        <div className="border-t border-[hsl(var(--border))] pt-3">
                            <div className="flex justify-between">
                                <span className="font-semibold">Totale</span>
                                <span className="font-display text-3xl" data-testid="checkout-total">€{amount}</span>
                            </div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] text-right">IVA inclusa</div>
                        </div>
                        <div className="mt-5 p-3 bg-[hsl(var(--accent))]/10 rounded-lg text-xs space-y-1">
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-[hsl(var(--accent))]" /> Disdici quando vuoi</div>
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-[hsl(var(--accent))]" /> Backup giornaliero</div>
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-[hsl(var(--accent))]" /> Supporto in italiano</div>
                            <div className="flex items-center gap-2 pt-1" data-testid="checkout-trial-refund">
                                <span className="text-[hsl(var(--accent))] font-bold leading-none">*</span>
                                <span className="font-bold">Prova di 7gg gratuita, rimborso 100%</span>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
